'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import type { GameDetails } from '../../../casino/types/types';
import { PAGE_SIZE } from '../constants';
import type { FetchGamesParams } from '../types';

type UseGamesFromUrlParams = {
  filterGames: (params: FetchGamesParams) => GameDetails[];
  isLoading: boolean;
  category?: string;
};

type UseGamesFromUrlReturn = {
  games: GameDetails[];
  setGames: React.Dispatch<React.SetStateAction<GameDetails[]>>;
  isFilteringGames: boolean;
  hasInitialized: boolean;
  searchParamsKey: string;
};

function useGamesFromUrl({ filterGames, isLoading, category }: UseGamesFromUrlParams): UseGamesFromUrlReturn {
  const searchParams = useSearchParams();
  const [games, setGames] = useState<GameDetails[]>([]);
  const [isFilteringGames, setIsFilteringGames] = useState(false);

  const prevParamsRef = useRef<{ searchParamsString: string } | null>(null);
  const categoryRef = useRef(category);

  useEffect(() => {
    categoryRef.current = category;
  }, [category]);

  useEffect(() => {
    const currentSearchParamsString = searchParams.toString();

    if (isLoading) {
      return;
    }

    if (!prevParamsRef.current || currentSearchParamsString !== prevParamsRef.current.searchParamsString) {
      setIsFilteringGames(true);

      const getGameParams: FetchGamesParams = {
        ...Object.fromEntries(searchParams.entries()),
        page: 1,
        pageSize: PAGE_SIZE,
        category: categoryRef.current ?? '',
      };

      queueMicrotask(() => {
        const filteredGames = filterGames(getGameParams);
        setGames(filteredGames);
        setIsFilteringGames(false);
        prevParamsRef.current = {
          searchParamsString: currentSearchParamsString,
        };
      });
    }
  }, [searchParams, filterGames, isLoading]);

  return {
    games,
    setGames,
    isFilteringGames,
    // React refs are values that are not needed for rendering. Refs should only be accessed outside of render, such as in event handlers or effects.
    // Accessing a ref value (the `current` property) during render can cause your component not to update as expected (https://react.dev/reference/react/useRef).
    // TODO: This should be fixed
    // eslint-disable-next-line react-hooks/refs
    hasInitialized: prevParamsRef.current !== null,
    searchParamsKey: searchParams.toString(),
  };
}

export { useGamesFromUrl };
export type { UseGamesFromUrlParams, UseGamesFromUrlReturn };
